// Minimal Twitch IRC parsing for the read-only chat monitor.
// A WebSocket frame from Twitch can carry MANY IRC lines at once — always
// parse per line, never per frame, or busy chats get undercounted.

export interface IrcPrivmsg {
  type: 'privmsg';
  channel: string;
  content: string;
}

export interface IrcPing {
  type: 'ping';
  payload: string;
}

export interface IrcWelcome {
  type: 'welcome';
}

export interface IrcReconnect {
  type: 'reconnect';
}

export type IrcEvent = IrcPrivmsg | IrcPing | IrcWelcome | IrcReconnect;

// One raw IRC line: [@tags ][:prefix ]COMMAND [params][ :trailing]
export function parseIrcLine(line: string): IrcEvent | null {
  let rest = line;
  if (!rest) return null;

  if (rest.startsWith('@')) {
    const sp = rest.indexOf(' ');
    if (sp === -1) return null;
    rest = rest.slice(sp + 1);
  }

  if (rest.startsWith(':')) {
    const sp = rest.indexOf(' ');
    if (sp === -1) return null;
    rest = rest.slice(sp + 1);
  }

  const sp = rest.indexOf(' ');
  const command = sp === -1 ? rest : rest.slice(0, sp);
  const params = sp === -1 ? '' : rest.slice(sp + 1);

  switch (command) {
    case 'PING':
      return { type: 'ping', payload: params.startsWith(':') ? params.slice(1) : params };
    case '001':
      return { type: 'welcome' };
    case 'RECONNECT':
      return { type: 'reconnect' };
    case 'PRIVMSG': {
      const m = params.match(/^#(\S+) :([\s\S]*)$/);
      if (!m) return null;
      return { type: 'privmsg', channel: m[1].toLowerCase(), content: m[2] };
    }
    default:
      return null;
  }
}

// One WebSocket frame → every interesting IRC event inside it.
export function parseIrcFrame(frame: string): IrcEvent[] {
  const events: IrcEvent[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (!line) continue;
    const event = parseIrcLine(line);
    if (event) events.push(event);
  }
  return events;
}
